import { UTMS } from '@/hub-shared'
import FingerprintJS from '@fingerprintjs/fingerprintjs'
import localForageHelper from '@helpers/localforage.helper'
import Environment from '@interfaces/environment.interface'
import { UserAuth, VerifyEmailResponse } from '@interfaces/user.interface'
import onEnvironmentChange from '@observables/environment.observable'
import LogService from '@services/log/log.service'
import MainService from '@services/main/main.service'
import { AxiosError, AxiosResponse } from 'axios'
import { take } from 'rxjs/operators'

export default class JessicaJonesService extends MainService {
  url: string
  logService = new LogService()

  constructor(jessicaJonesUrl?: string) {
    super()
    if (!jessicaJonesUrl) {
      onEnvironmentChange.pipe(take(1)).subscribe((environment: Environment) => {
        this.url = environment?.squidApis?.jessicaJones
      })
    } else {
      this.url = jessicaJonesUrl
    }
    this.logService.init('Jessica Jones Service')
  }

  public async generateJWT(code: string, provider: string): Promise<UserAuth> {
    const headers = await this.getHeaders(false)
    const fingerObject = await (await FingerprintJS.load()).get()
    const fingerprint = (await localForageHelper.getItem('fingerprint')) || `${fingerObject?.visitorId}.${new Date().getTime()}`
    localForageHelper.setItem('fingerprint', fingerprint)

    return this.http
      .post(
        `${this.url}/generate/jwt/firebase`,
        {
          fingerprint,
          code,
          provider,
        },
        {
          headers,
        }
      )
      .then((r: AxiosResponse) => r.data || {})
      .catch((error: any) => {
        this.logService.error({
          message: 'Error login with firebase',
          error,
        })
        throw error.response
      })
  }

  public async migrate(email: string, password: string) {
    if (!email || !password) {
      throw new Error('Email and password are required')
    }
    const headers = await this.getHeaders(false)
    return this.http
      .post(
        `${this.url}/login/migration`,
        {
          email,
          password,
        },
        {
          headers,
        }
      )
      .then((r: AxiosResponse) => r.data || {})
      .catch((error: any) => {
        this.logService.error({
          message: 'Error on generate JWT',
          error,
        })
        throw error?.response?.data || error
      })
  }

  public async generateImpersonate(impersonateToken: string) {
    const headers = await this.getHeaders(true, impersonateToken)
    const fingerObject = await (await FingerprintJS.load()).get()
    const fingerprint = (await localForageHelper.getItem('fingerprint')) || `${fingerObject?.visitorId}.${new Date().getTime()}`
    localForageHelper.setItem('fingerprint', fingerprint)

    return this.http
      .post(
        `${this.url}/generate/jwt/impersonate`,
        {
          fingerprint,
        },
        {
          headers,
        }
      )
      .then((r: AxiosResponse) => r.data || {})
      .catch((error: any) => {
        this.logService.error({
          message: 'Error on generate JWT Impersonate',
          error,
        })
        throw error.response
      })
  }

  public async resetPassword(token: string, pwd: string): Promise<void> {
    const headers = await this.getHeaders(true, token)
    return await this.http
      .patch(`${this.url}/hub/reset-password`, { pwd }, { headers })
      .then((r: AxiosResponse) => r.data || {})
      .catch((error: any) => {
        this.logService.error({
          message: 'Could not change password',
          error,
        })
        throw error.response
      })
  }

  public async backendLogin(email?: string, pwd?: string, token?: string, provider?: string, returnUser = false) {
    if (!(token && provider) && (!email || !pwd)) {
      throw new Error('Email and password are required')
    }

    const headers = await this.getHeaders(false)
    return this.http
      .post(
        `${this.url}/verify/login?returnUser=${returnUser}`,
        {
          email,
          pwd,
          token,
          provider,
        },
        {
          headers,
        }
      )
      .then((r: AxiosResponse) => r.data || {})
      .catch((error: any) => {
        this.logService.error({
          message: 'Error on verify',
          error,
        })
        throw error.response
      })
  }

  public async passwordRequest(email: string): Promise<{ message: string; stepComponent: string }> {
    const headers = await this.getHeaders(false, '', false)
    return this.http
      .post(`${this.url}/hub/send-email-reset-password/${email}`, {}, { headers })
      .then((r: AxiosResponse) => r?.data)
      .catch((error) => {
        this.logService.error({ message: 'Failed to get request Password', error: error })
        throw error.response
      })
  }

  public async verifyEmail(email?: string, code?: string, provider?: string, utms?: UTMS): Promise<VerifyEmailResponse> {
    const headers = await this.getHeaders(false)

    if (!email && !code && !provider) {
      throw new Error('Email is required')
    }

    return this.http
      .post(`${this.url}/email/verification`, { email, code, provider, utms }, { headers })
      .then((r: AxiosResponse) => r || {})
      .catch((error: AxiosError) => {
        this.logService.error({
          message: 'Error on verify email',
          error,
        })
        throw error.response
      })
  }

  public async refreshToken() {
    const headers = await this.getHeaders(true)

    return this.http
      .post(`${this.url}/token/refresh`, {}, { headers })
      .then((r: AxiosResponse) => r.data || {})
      .catch((error: any) => {
        this.logService.error({
          message: 'Error on refresh token',
          error,
        })
        throw error.response
      })
  }
}
